// shared/context/AuthContext.tsx

import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { getHomePath } from '../utils/homePath';

// Opción minimalista (solo lo que uses en el frontend)
interface User {
  /** `_id` del usuario en MongoDB (hex de 24 caracteres). */
  userId: string;
  name: string;
  surname: string;
  email: string;
  role: string;
  groupId?: string | null;
  accountId?: string | null;
  practitionerIdentifier?: string | null;
  practitionerIdentifierSystem?: string | null;
  professionalTitle?: string | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Cargar usuario desde localStorage al iniciar
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('user');
    
    if (token && storedUser) {
      try {
        const userData = JSON.parse(storedUser);

        // Sesiones anteriores a la migración a MongoDB guardaban el
        // identificador como `code` y el JWT llevaba el claim `code`.
        // Ese token ya no es válido contra el backend, así que en lugar de
        // dejar al usuario con `userId` undefined (fallos silenciosos),
        // purgamos la sesión y lo mandamos al login.
        if (typeof userData.userId !== 'string' || userData.userId === '') {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          setIsLoading(false);
          return;
        }

        // Sesiones anteriores a feature-112.8 guardaban el ámbito como
        // `centerId`. Si no hay `groupId` y sí queda la clave legacy,
        // purgamos: rehidratar en silencio dejaría al usuario sin ámbito
        // (H-09 / AC-10). Hay que leer `centerId` literal; no existe en User.
        if (
          Object.prototype.hasOwnProperty.call(userData, 'centerId')
          && (userData.groupId == null || userData.groupId === '')
        ) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          setIsLoading(false);
          return;
        }

        // Normalizar el rol a minúsculas
        if (userData.role) {
          userData.role = userData.role.toLowerCase();
        }
        setUser(userData);
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      }
    }
    setIsLoading(false);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    // Best-effort: borrar también la cookie admin_session_jwt si existe,
    // para que un siguiente usuario en el mismo navegador no pueda entrar
    // a /api/admin/ con credenciales del anterior.
    // No usamos await: si falla (offline, etc.) no debe bloquear el logout.
    void fetch('/api/admin-session', { method: 'DELETE', credentials: 'include' }).catch(() => {});
    void fetch('/api/admin/logout', { credentials: 'include' }).catch(() => {});
    setUser(null);
    navigate('/login', { replace: true });
  }, [navigate]);

  const login = useCallback((token: string, userData: User) => {
    // Normalizar el rol a minúsculas
    const normalizedUser = {
      ...userData,
      role: userData.role.toLowerCase()
    };
    localStorage.setItem('authToken', token);
    localStorage.setItem('user', JSON.stringify(normalizedUser));
    setUser(normalizedUser);

    // Check for a pending launch redirect (user clicked "Open in Ainovis"
    // from dcm4chee / RIS worklist before being logged in)
    const launchToken = sessionStorage.getItem('launchToken');
    if (launchToken) {
      const launchRedirect = sessionStorage.getItem('launchRedirect') || `?token=${launchToken}`;
      // NOTE: Don't clear sessionStorage here — LoginPage's useEffect may still
      // fire after setUser() and would race with this navigate. LaunchPage
      // will clean up sessionStorage after reading the token from the URL.
      navigate(`/launch${launchRedirect}`, { replace: true });
    } else {
      navigate(getHomePath(normalizedUser.role), { replace: true });
    }
  }, [navigate]);

  const value: AuthContextType = {
    user,
    isAuthenticated: user !== null,
    isLoading,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
}